import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Footer, MarketingFinalCta } from '../components/homepage';
import { DEMO_REQUEST_MAILTO, INTEREST_LIST_MAILTO } from '../config/contact';
import { getWhitePaperBySlug } from '../content/whitePapers';
import {
  marketingCardSx,
  marketingDisplayFont,
  marketingHeroGridSx,
  marketingMutedSectionSx,
  marketingPalette,
  marketingPrimaryButtonSx,
  marketingSecondaryButtonSx,
  marketingSectionSx,
} from '../components/homepage/marketingSystem';

const WhitePaperPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const whitePaper = slug ? getWhitePaperBySlug(slug) : undefined;

  if (!whitePaper) {
    return <Navigate to="/white-papers" replace />;
  }

  return (
    <Box className="marketing-layout" sx={{ width: '100%', overflowX: 'hidden' }}>
      <Helmet>
        <link rel="canonical" href={`https://ormal.io/white-papers/${slug}`} />
      </Helmet>
      {/* ============================== HERO ============================== */}
      <Box component="section" sx={{ ...marketingSectionSx, pt: { xs: 6, md: 9 }, pb: { xs: 5, md: 6 }, overflow: 'hidden' }}>
        <Box sx={marketingHeroGridSx} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            component={RouterLink}
            to="/white-papers"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              mb: 2.2,
              fontSize: '0.82rem',
              fontWeight: 400,
              letterSpacing: '0.02em',
              color: marketingPalette.muted,
              textDecoration: 'none',
              transition: 'color 150ms ease',
              '&:hover': { color: marketingPalette.accent },
            }}
          >
            <ArrowBack sx={{ fontSize: '0.95rem' }} />
            All white papers
          </Box>

          <Box sx={{ maxWidth: 860 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  fontWeight: 400,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: marketingPalette.accent,
                }}
              >
                White Paper
              </Typography>
              <Box sx={{ width: 24, height: 1, backgroundColor: 'rgba(15,111,255,0.4)' }} />
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 400,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: marketingPalette.muted,
                }}
              >
                {whitePaper.label}
              </Typography>
            </Box>

            <Typography
              component="h1"
              sx={{
                fontFamily: marketingDisplayFont,
                fontSize: { xs: '2.15rem', md: '3.1rem' },
                fontWeight: 400,
                letterSpacing: '-0.045em',
                lineHeight: 1.08,
                color: marketingPalette.headline,
                textWrap: 'balance',
                mb: 1.9,
              }}
            >
              {whitePaper.pageTitle}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1.05rem', md: '1.18rem' },
                lineHeight: 1.55,
                color: marketingPalette.body,
                textWrap: 'pretty',
                maxWidth: 720,
                mb: 3,
              }}
            >
              {whitePaper.summary}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.4 }}>
              <Button href={DEMO_REQUEST_MAILTO} sx={marketingPrimaryButtonSx}>
                Request Demo
              </Button>
              <Button component={RouterLink} to="/white-papers" sx={marketingSecondaryButtonSx}>
                All papers
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ==================== HIGHLIGHT STRIP (INLINE) ==================== */}
      <Box sx={{ pb: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {whitePaper.highlights.map((item, index) => (
              <Box
                key={`${item.label}-${item.value}`}
                sx={{
                  position: 'relative',
                  px: { xs: 2.5, md: 2.8 },
                  py: { xs: 2.4, md: 2.6 },
                  borderRadius: 1.25,
                  border: '1px solid rgba(196,212,232,0.96)',
                  background:
                    index === 0
                      ? 'linear-gradient(180deg, #ffffff 0%, #f4f9ff 100%)'
                      : 'linear-gradient(180deg, #ffffff 0%, #fafbfd 100%)',
                  boxShadow: '0 14px 34px rgba(15,23,42,0.045)',
                  overflow: 'hidden',
                  '&::before':
                    index === 0
                      ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          background: 'linear-gradient(90deg, #0f6fff, #0ea5e9)',
                        }
                      : undefined,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 400,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: index === 0 ? marketingPalette.accent : marketingPalette.muted,
                    mb: 1.1,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: marketingDisplayFont,
                    fontSize: { xs: '2.2rem', md: '2.6rem' },
                    fontWeight: 400,
                    letterSpacing: '-0.04em',
                    lineHeight: 1,
                    color: marketingPalette.headline,
                    fontVariantNumeric: 'tabular-nums',
                    mb: 1,
                  }}
                >
                  {item.value}
                </Typography>
                <Typography sx={{ color: marketingPalette.body, fontSize: '0.88rem', lineHeight: 1.55 }}>
                  {item.detail}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ======================== INTRO NARRATIVE ========================= */}
      <Box component="section" sx={{ ...marketingMutedSectionSx, py: { xs: 5.5, md: 7 } }}>
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'inline-block',
              mb: 1.75,
              px: 1.4,
              py: 0.45,
              borderRadius: '999px',
              backgroundColor: marketingPalette.accentSoft,
              border: `1px solid ${marketingPalette.accentBorder}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 400,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: marketingPalette.accent,
              }}
            >
              What this paper argues
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gap: 1.6 }}>
            {whitePaper.introParagraphs.map((paragraph, index) => (
              <Typography
                key={`${whitePaper.slug}-intro-${index}`}
                sx={{
                  color: index === 0 ? marketingPalette.headline : marketingPalette.body,
                  fontWeight: index === 0 ? 600 : 400,
                  lineHeight: index === 0 ? 1.55 : 1.82,
                  fontSize: index === 0 ? { xs: '1.12rem', md: '1.22rem' } : '1rem',
                  letterSpacing: index === 0 ? '-0.005em' : 0,
                }}
              >
                {paragraph}
              </Typography>
            ))}
          </Box>

          {/* Meta ribbon at the end of the intro */}
          <Box
            sx={{
              mt: 4,
              pt: 2.5,
              borderTop: '1px solid rgba(196,212,232,0.7)',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 1.5, md: 2.5 },
            }}
          >
            {whitePaper.meta.map((item) => (
              <Box key={item.label}>
                <Typography
                  sx={{
                    fontSize: '0.66rem',
                    fontWeight: 400,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: marketingPalette.muted,
                    mb: 0.6,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, lineHeight: 1.58, fontSize: '0.94rem' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* =========================== BODY SECTIONS ======================== */}
      {whitePaper.sections.map((section, index) => {
        const isMuted = index % 2 === 1;
        const sectionSx = isMuted ? marketingMutedSectionSx : marketingSectionSx;

        return (
          <Box component="section" sx={sectionSx} key={section.title}>
            <Container maxWidth="lg">
              {/* Section header — centered editorial block */}
              <Box sx={{ maxWidth: 740, mb: { xs: 3.5, md: 4.5 } }}>
                {section.eyebrow ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.4 }}>
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.72rem',
                        fontWeight: 400,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: marketingPalette.accent,
                      }}
                    >
                      {section.eyebrow}
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        maxWidth: 72,
                        height: 1,
                        background: 'linear-gradient(90deg, rgba(15,111,255,0.4), rgba(15,111,255,0))',
                      }}
                    />
                  </Box>
                ) : null}
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: marketingDisplayFont,
                    fontSize: { xs: '1.75rem', md: '2.35rem' },
                    fontWeight: 400,
                    letterSpacing: '-0.04em',
                    color: marketingPalette.headline,
                    lineHeight: 1.12,
                    textWrap: 'balance',
                    mb: section.description ? 1.3 : 0,
                  }}
                >
                  {section.title}
                </Typography>
                {section.description ? (
                  <Typography sx={{ color: marketingPalette.body, lineHeight: 1.72, fontSize: { xs: '1rem', md: '1.06rem' } }}>
                    {section.description}
                  </Typography>
                ) : null}
              </Box>

              {/* Section body */}
              <Box sx={{ display: 'grid', gap: { xs: 2.5, md: 3 } }}>
                {/* Paragraphs: centered prose column */}
                {section.paragraphs?.length ? (
                  <Box sx={{ maxWidth: 780, display: 'grid', gap: 2 }}>
                    {section.paragraphs.map((paragraph) => (
                      <Typography
                        key={paragraph}
                        sx={{
                          color: marketingPalette.body,
                          lineHeight: 1.86,
                          fontSize: { xs: '1rem', md: '1.04rem' },
                        }}
                      >
                        {paragraph}
                      </Typography>
                    ))}
                  </Box>
                ) : null}

                {/* Cards: themed grid with accent top on first */}
                {section.cards ? (
                  <Grid container spacing={2}>
                    {section.cards.map((item, cardIndex) => {
                      const columns = section.cards!.length === 2 ? 6 : section.cards!.length >= 4 ? 6 : 4;
                      return (
                        <Grid item xs={12} md={columns} key={item.title}>
                          <Box
                            sx={{
                              height: '100%',
                              position: 'relative',
                              border: '1px solid rgba(196,212,232,0.96)',
                              borderRadius: 1.25,
                              background:
                                cardIndex === 0
                                  ? 'linear-gradient(180deg, #ffffff 0%, #f4f9ff 100%)'
                                  : '#ffffff',
                              boxShadow: '0 14px 30px rgba(15,23,42,0.045)',
                              px: { xs: 2.4, md: 2.8 },
                              py: { xs: 2.4, md: 2.7 },
                              overflow: 'hidden',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 3,
                                background:
                                  cardIndex === 0
                                    ? 'linear-gradient(90deg, #0f6fff, #0ea5e9)'
                                    : 'rgba(15,111,255,0.22)',
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.66rem',
                                fontWeight: 400,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: cardIndex === 0 ? marketingPalette.accent : marketingPalette.muted,
                                mb: 1.2,
                              }}
                            >
                              {`0${cardIndex + 1} · ${cardIndex === 0 ? 'Key point' : 'Supporting'}`}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 400,
                                color: marketingPalette.headline,
                                fontSize: { xs: '1.02rem', md: '1.1rem' },
                                lineHeight: 1.3,
                                mb: 1.1,
                              }}
                            >
                              {item.title}
                            </Typography>
                            <Typography sx={{ color: marketingPalette.body, lineHeight: 1.78, fontSize: '0.96rem' }}>
                              {item.body}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : null}

                {/* Table: modernized, no uppercase, striped rows */}
                {section.table ? (
                  <Card
                    sx={{
                      ...marketingCardSx,
                      p: 0,
                      overflow: 'hidden',
                      '&:hover': { transform: 'none' },
                    }}
                  >
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            {section.table.columns.map((column, colIndex) => (
                              <TableCell
                                key={column}
                                sx={{
                                  py: 1.8,
                                  px: 2.25,
                                  borderBottom: '1px solid rgba(196,212,232,0.8)',
                                  color: marketingPalette.headline,
                                  fontWeight: 400,
                                  fontSize: '0.84rem',
                                  letterSpacing: '-0.005em',
                                  backgroundColor: '#f6f9fd',
                                  borderLeft: colIndex === 0 ? `3px solid ${marketingPalette.accent}` : 'none',
                                }}
                              >
                                {column}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.table.rows.map((row, rowIndex) => (
                            <TableRow
                              key={row.join('|')}
                              sx={{
                                backgroundColor: rowIndex % 2 === 1 ? '#fbfcfe' : 'transparent',
                                transition: 'background-color 150ms ease',
                                '&:hover': { backgroundColor: '#f4f9ff' },
                              }}
                            >
                              {row.map((cell, cellIndex) => (
                                <TableCell
                                  key={`${row[0]}-${cellIndex}`}
                                  sx={{
                                    py: 1.85,
                                    px: 2.25,
                                    verticalAlign: 'top',
                                    borderBottom: '1px solid rgba(219,228,239,0.6)',
                                    color: cellIndex === 0 ? marketingPalette.headline : marketingPalette.body,
                                    fontWeight: cellIndex === 0 ? 700 : 400,
                                    lineHeight: 1.7,
                                    fontSize: '0.93rem',
                                    borderLeft: cellIndex === 0 ? `3px solid rgba(15,111,255,0.18)` : 'none',
                                  }}
                                >
                                  {cell}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                ) : null}

                {/* Bullets: cleaner numbered list with accent markers */}
                {section.bullets ? (
                  <Box sx={{ maxWidth: 820, display: 'grid', gap: 1.5 }}>
                    {section.bullets.map((item, bulletIndex) => (
                      <Box
                        key={item}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '36px minmax(0, 1fr)',
                          gap: 1.6,
                          alignItems: 'start',
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: marketingPalette.accentSoft,
                            border: `1px solid ${marketingPalette.accentBorder}`,
                            color: marketingPalette.accent,
                            fontSize: '0.78rem',
                            fontWeight: 400,
                            fontFamily: 'monospace',
                            mt: 0.15,
                          }}
                        >
                          {bulletIndex + 1}
                        </Box>
                        <Typography sx={{ color: marketingPalette.body, lineHeight: 1.78, fontSize: '0.98rem', pt: 0.15 }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Box>
            </Container>
          </Box>
        );
      })}

      {/* ============================ REFERENCES ========================== */}
      {whitePaper.references.length ? (
        <Box sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 5, md: 6 } }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                borderTop: '1px solid rgba(219,228,239,0.9)',
                pt: 2.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.4 }}>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.68rem',
                    fontWeight: 400,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: marketingPalette.muted,
                  }}
                >
                  References
                </Typography>
                <Box sx={{ flex: 1, height: 1, backgroundColor: 'rgba(196,212,232,0.6)' }} />
              </Box>
              <Box
                component="ol"
                sx={{
                  m: 0,
                  pl: 2.2,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: { xs: 0.7, md: 1 },
                }}
              >
                {whitePaper.references.map((item) => (
                  <Typography
                    key={item}
                    component="li"
                    sx={{
                      color: marketingPalette.muted,
                      lineHeight: 1.62,
                      fontSize: '0.82rem',
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Container>
        </Box>
      ) : null}

      <MarketingFinalCta
        kicker="White paper · Next step"
        heading="If this matches what you're seeing, let's look at your data."
        body="We'll name what's hurting performance today, show where Ormal would start in your stack, and decide together whether a health check or a demo is the faster next step."
        secondary={{ label: 'Join interest list', href: INTEREST_LIST_MAILTO }}
      />

      <Footer />
    </Box>
  );
};

export default WhitePaperPage;
